export const BLOCKED_PARTICIPANT_ERROR_CODE = 'transaction-participant-unavailable';
export const BLOCKED_PARTICIPANT_ERROR_MESSAGE = 'This conversation is no longer available.';

const UNAVAILABLE_USER_STATES = [
  'banned',
  'closed',
  'deleted',
  'disabled',
  'inactive',
  'pending-approval',
  'pendingapproval',
  'restricted',
];
const TRANSACTION_PARTICIPANT_ROLES = ['customer', 'provider'];
const PENDING_REVIEW_MESSAGE_STATUSES = ['pendingReview', 'moderationPending', 'needsApproval'];

export const getEntityUuid = entityOrId => {
  if (!entityOrId) {
    return null;
  }

  if (typeof entityOrId === 'string') {
    return entityOrId;
  }

  return entityOrId?.uuid || entityOrId?.id?.uuid || entityOrId?.id || null;
};

export const isUserUnavailable = user => {
  const attributes = user?.attributes;

  if (!attributes) {
    return false;
  }

  const state = typeof attributes.state === 'string' ? attributes.state.toLowerCase() : null;

  return (
    attributes.banned === true ||
    attributes.deleted === true ||
    attributes.disabled === true ||
    UNAVAILABLE_USER_STATES.includes(state)
  );
};

export const getUnavailableTransactionParticipant = transaction => {
  const participants = TRANSACTION_PARTICIPANT_ROLES.map(role => ({
    role,
    user: transaction?.[role],
  }));

  return participants.find(({ user }) => isUserUnavailable(user)) || null;
};

export const hasUnavailableTransactionParticipant = transaction =>
  Boolean(getUnavailableTransactionParticipant(transaction));

export const getUnavailableMessageParticipant = message => {
  const sender = message?.sender;
  return isUserUnavailable(sender) ? { role: 'sender', user: sender } : null;
};

export const isMessageSenderUnavailable = message =>
  Boolean(getUnavailableMessageParticipant(message));

export const isMessagePendingReview = message => {
  const attributes = message?.attributes || {};
  const status =
    attributes.status ||
    attributes.moderationStatus ||
    attributes.metadata?.status ||
    attributes.metadata?.moderationStatus ||
    attributes.protectedData?.status ||
    attributes.protectedData?.moderationStatus;

  return PENDING_REVIEW_MESSAGE_STATUSES.includes(status);
};

export const createParticipantUnavailableError = () => {
  const error = new Error(BLOCKED_PARTICIPANT_ERROR_MESSAGE);
  error.code = BLOCKED_PARTICIPANT_ERROR_CODE;
  error.status = 403;
  error.statusCode = 403;
  return error;
};

export const unavailableParticipantLogData = (transaction, unavailableParticipant) => {
  const user = unavailableParticipant?.user;
  const attributes = user?.attributes || {};

  return {
    txId: getEntityUuid(transaction),
    participantRole: unavailableParticipant?.role || null,
    participantId: getEntityUuid(user),
    participantState: attributes.state || null,
    participantBanned: attributes.banned === true,
    participantDeleted: attributes.deleted === true,
    participantDisabled: attributes.disabled === true,
  };
};

const includedUsersById = included =>
  (included || [])
    .filter(entity => entity?.type === 'user')
    .reduce((acc, entity) => {
      const userId = getEntityUuid(entity);

      if (userId) {
        acc.set(userId, entity);
      }

      return acc;
    }, new Map());

const unavailableParticipantFromRawTransaction = (transaction, includedUsers) => {
  return TRANSACTION_PARTICIPANT_ROLES.reduce((unavailableParticipant, role) => {
    if (unavailableParticipant) {
      return unavailableParticipant;
    }

    const directUser = transaction?.[role];
    if (isUserUnavailable(directUser)) {
      return { role, user: directUser };
    }

    const relationship = transaction?.relationships?.[role];
    const participantRef = relationship?.data;
    const participantId = getEntityUuid(participantRef);
    const includedUser = participantId ? includedUsers.get(participantId) : null;
    const isExplicitlyMissing = relationship && participantRef == null;
    const isMissingIncludedUser = participantId && !includedUser && !directUser;

    if (includedUser && isUserUnavailable(includedUser)) {
      return { role, user: includedUser };
    }

    if (!isExplicitlyMissing && !isMissingIncludedUser) {
      return null;
    }

    return {
      role,
      user: {
        id: participantRef?.id || participantRef || null,
        type: 'user',
        attributes: {
          state: 'unavailable',
          disabled: true,
        },
      },
    };
  }, null);
};

export const unavailableMessageParticipantLogData = (message, unavailableParticipant, txId) => {
  const user = unavailableParticipant?.user;
  const attributes = user?.attributes || {};

  return {
    txId: getEntityUuid(txId),
    messageId: getEntityUuid(message),
    participantRole: unavailableParticipant?.role || null,
    participantId: getEntityUuid(user),
    participantState: attributes.state || null,
    participantBanned: attributes.banned === true,
    participantDeleted: attributes.deleted === true,
    participantDisabled: attributes.disabled === true,
  };
};

export const splitTransactionsByParticipantAvailability = (
  rawTransactions = [],
  denormalizedTransactions = [],
  included = []
) => {
  const hiddenById = denormalizedTransactions.reduce((acc, transaction) => {
    const unavailableParticipant = getUnavailableTransactionParticipant(transaction);
    const txId = getEntityUuid(transaction);

    if (txId && unavailableParticipant) {
      acc.set(txId, { transaction, unavailableParticipant });
    }

    return acc;
  }, new Map());
  const includedUsers = includedUsersById(included);

  rawTransactions.forEach(transaction => {
    const txId = getEntityUuid(transaction);
    const unavailableParticipant = unavailableParticipantFromRawTransaction(
      transaction,
      includedUsers
    );

    if (txId && unavailableParticipant && !hiddenById.has(txId)) {
      hiddenById.set(txId, { transaction, unavailableParticipant });
    }
  });

  const visibleTransactions = rawTransactions.filter(transaction => {
    const txId = getEntityUuid(transaction);
    return !txId || !hiddenById.has(txId);
  });

  return {
    visibleTransactions,
    hiddenTransactions: Array.from(hiddenById.values()),
  };
};

export const filterMessagesBySenderAvailability = (messages = []) => {
  return messages.reduce(
    (acc, message) => {
      const unavailableParticipant = getUnavailableMessageParticipant(message);

      if (isMessagePendingReview(message)) {
        acc.hiddenMessages.push({ message, reason: 'pending-review' });
      } else if (unavailableParticipant) {
        acc.hiddenMessages.push({ message, unavailableParticipant });
      } else {
        acc.visibleMessages.push(message);
      }

      return acc;
    },
    { visibleMessages: [], hiddenMessages: [] }
  );
};
